import { useState, useEffect, useCallback } from 'react';
import { fetchClickHistory, fetchButtons, deleteClickEvent } from '../services/api';
import { ClickEvent, CustomButton } from '../types';
import { useManualData } from './useManualData';
import { config } from '../config';

interface UseHistoryProps {
  counterIdentifier: string;
  isOwnerUrl: boolean;
  selectedButton: string;
  defaultItemsPerPage?: number;
}

/**
 * Custom hook for fetching and managing history data
 * Uses manual refresh approach for better UX without any polling
 */
export function useHistory({ counterIdentifier, isOwnerUrl, selectedButton, defaultItemsPerPage = config.pagination.defaultItemsPerPage }: UseHistoryProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [eventToDelete, setEventToDelete] = useState<ClickEvent | null>(null);

  // Use useManualData to fetch history data with proper loading states but no polling
  const historyData = useManualData<ClickEvent[]>({
    fetchFn: async () => {
      const data = await fetchClickHistory(
        counterIdentifier, 
        selectedButton === 'all' ? undefined : selectedButton,
        isOwnerUrl
      );
      
      // Sort by timestamp in descending order (newest first)
      return [...data].sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    },
    initialData: [],
    dependencies: [counterIdentifier, selectedButton, isOwnerUrl]
  });

  // Use useManualData for buttons data with no polling
  const buttonsData = useManualData<CustomButton[]>({
    fetchFn: async () => fetchButtons(counterIdentifier, isOwnerUrl),
    initialData: [],
    dependencies: [counterIdentifier, isOwnerUrl]
  });

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [historyData.data?.length]);

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Get paginated data
  const getPaginatedData = () => {
    const clickEvents = historyData.data || [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, clickEvents.length);
    return {
      data: clickEvents.slice(startIndex, endIndex),
      startIndex,
      endIndex
    };
  };

  const paginatedData = getPaginatedData();

  return {
    clickEvents: historyData.data || [],
    buttons: buttonsData.data || [],
    paginatedEvents: paginatedData.data,
    paginationInfo: {
      startIndex: paginatedData.startIndex,
      endIndex: paginatedData.endIndex
    },
    loading: historyData.loading,
    refreshing: historyData.refreshing || buttonsData.refreshing,
    error: historyData.error,
    currentPage,
    itemsPerPage,
    eventToDelete,
    setEventToDelete,
    setCurrentPage,
    handleItemsPerPageChange,
    refetchHistory: historyData.refetch,
    refetchButtons: buttonsData.refetch,
    totalPages: Math.ceil((historyData.data?.length || 0) / itemsPerPage)
  };
} 